import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LogoMark } from "../components/Logo";
import { AdminInvoices } from "../components/AdminInvoices";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_STYLES = {
  NEW: "text-alert border-alert/50 bg-alert/10",
  SCHEDULED: "text-khaki border-khaki/50 bg-khaki/10",
  COMPLETED: "text-olive border-olive bg-olive/20",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/requests");
      setRequests(res.data);
    } catch (err) {
      toast.error("FAILED TO LOAD REQUESTS", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/requests/${id}`, { status });
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`STATUS: ${status}`, { description: `Request ${id.slice(0, 8).toUpperCase()} updated.` });
    } catch (err) {
      toast.error("UPDATE FAILED", { description: formatApiError(err) });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const count = (s) => requests.filter((r) => r.status === s).length;

  return (
    <div className="min-h-screen bg-obsidian text-foreground" data-testid="admin-dashboard">
      <header className="border-b border-olive-deep bg-obsidian/95 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <div className="leading-none">
              <span className="font-stencil text-sm text-foreground">COMMAND CENTER</span>
              <span className="block text-[9px] tracking-[0.3em] text-coyote mt-1">OPERATION FIX-IT</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-[10px] tracking-[0.2em] text-coyote" data-testid="admin-user-label">
              {user?.email}
            </span>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-xs tracking-[0.2em] border border-olive-deep px-4 py-2 text-khaki hover:bg-alert hover:text-white hover:border-alert transition-colors duration-300"
            >
              [ LOGOUT ]
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
        <p className="overline-tactical text-coyote mb-4">/// INCOMING TRANSMISSIONS</p>
        <h1 className="font-display uppercase text-3xl sm:text-5xl tracking-tight mb-10" data-testid="admin-heading">
          Service Requests
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-olive-deep border border-olive-deep mb-10" data-testid="admin-stats">
          {[
            ["TOTAL", requests.length, "text-foreground"],
            ["NEW", count("NEW"), "text-alert"],
            ["SCHEDULED", count("SCHEDULED"), "text-khaki"],
            ["COMPLETED", count("COMPLETED"), "text-olive"],
          ].map(([label, value, cls]) => (
            <div key={label} className="bg-obsidian p-5" data-testid={`stat-${label.toLowerCase()}`}>
              <div className={`font-display text-4xl ${cls}`}>{value}</div>
              <div className="text-[9px] tracking-[0.3em] text-coyote mt-1">{label}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-coyote tracking-[0.2em]" data-testid="requests-loading">DECRYPTING TRANSMISSIONS...</p>
        ) : requests.length === 0 ? (
          <div className="border border-olive-deep p-14 text-center" data-testid="requests-empty">
            <p className="font-display uppercase text-2xl text-khaki">No Requests On File</p>
            <p className="mt-3 text-xs text-coyote tracking-[0.2em]">INCOMING SERVICE REQUESTS WILL APPEAR HERE</p>
          </div>
        ) : (
          <div className="border border-olive-deep overflow-x-auto" data-testid="requests-table">
            <table className="w-full text-left text-xs min-w-[900px]">
              <thead>
                <tr className="bg-olive-deep/40 text-coyote">
                  {["ID", "RECEIVED", "CLIENT", "CONTACT", "OPERATION", "TARGET DATE", "ADDRESS", "DETAILS", "STATUS"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] tracking-[0.25em] font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-olive-deep">
                {requests.map((r) => (
                  <tr key={r.id} className="hover:bg-olive-deep/20 transition-colors duration-200" data-testid={`request-row-${r.id.slice(0, 8)}`}>
                    <td className="px-4 py-3 font-mono text-coyote whitespace-nowrap">{r.id.slice(0, 8).toUpperCase()}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {r.created_at ? format(new Date(r.created_at), "MM/dd/yy HH:mm") : "-"}
                    </td>
                    <td className="px-4 py-3 font-bold text-foreground whitespace-nowrap">{r.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <div className="whitespace-nowrap">{r.phone}</div>
                      <div className="text-coyote">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 text-khaki font-bold whitespace-nowrap">{r.service_type}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{r.preferred_date}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.address}</td>
                    <td className="px-4 py-3 text-muted-foreground max-w-[220px]">
                      <span className="line-clamp-2">{r.description}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger
                          className={`rounded-none h-8 w-[130px] text-[10px] tracking-[0.15em] font-bold border ${STATUS_STYLES[r.status]}`}
                          data-testid={`status-select-${r.id.slice(0, 8)}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none bg-obsidian border-olive-deep">
                          {["NEW", "SCHEDULED", "COMPLETED"].map((s) => (
                            <SelectItem key={s} value={s} className="text-xs" data-testid={`status-option-${s.toLowerCase()}`}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AdminInvoices />
      </main>
    </div>
  );
}
